import { Editor, HistoryEntry, TLRecord, TLShape, TLShapeId } from 'tldraw';

import { VisualisationPendingChanges } from './visualisationPendingChanges';
import { VisualChronologyNodeShape } from '../components/TimelineElements/VisualChronologyNode';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';

export interface VisualisationChangeHandlerOptions {
  matterId: string;
  subsetId: string;
  chronologyId: string;
  onChangesUpdated: () => void;
}

export class VisualisationChangeHandler {
  private editor: Editor;
  private options: VisualisationChangeHandlerOptions;
  private originalNodes: Map<string, VisualChronologyNodeShape> = new Map();
  private isInitialized = false;
  private isPaused = true; // Start paused to avoid detecting initial timeline creation
  private storeUnsubscribe: (() => void) | null = null;
  private pendingChanges: VisualisationPendingChanges;

  constructor(editor: Editor, options: VisualisationChangeHandlerOptions) {
    this.editor = editor;
    this.options = options;
    this.pendingChanges = new VisualisationPendingChanges();
    this.initialize();
  }

  private initialize() {
    if (this.isInitialized) return;

    // Add the onupdate listener to the list of listeners
    this.pendingChanges.addListener(() => {
      this.options.onChangesUpdated();
    });

    this.isInitialized = true;
  }

  private captureInitialState() {
    const nodes = this.editor.getCurrentPageShapes().filter((shape) => shape.type === 'visual-chronology-node');
    nodes.forEach((node) => {
      this.originalNodes.set(node.id, node as VisualChronologyNodeShape);
    });
  }

  // This function is called when TLDraw detects a change in its store
  private handleStoreChange(entry: HistoryEntry<TLRecord>) {
    // Don't process changes if paused
    if (this.isPaused) {
      return;
    }

    if (entry.changes?.added) {
      this.handleAddedShapes(entry.changes.added);
    }

    if (entry.changes?.updated) {
      this.handleUpdatedShapes(entry.changes.updated);
    }

    if (entry.changes?.removed) {
      this.handleRemovedShapes(entry.changes.removed);
    }
  }

  private handleAddedShapes(added: Record<TLShapeId, TLRecord>) {
    Object.values(added).forEach((record: TLRecord) => {
      const shape = record as TLShape;
      if (shape.type === 'visual-chronology-node') {
        this.handleNodeAdded(shape as VisualChronologyNodeShape);

        trackCustomEvent(TrackingEventsV2.AddVisualChronologyNode, {
          matterId: this.options.matterId,
          subsetId: this.options.subsetId,
          chronologyId: this.options.chronologyId,
        });
      } else if (this.isAnnotationShape(shape.type)) {
        this.handleAnnotationAdded(shape);
      }
    });
  }

  private handleUpdatedShapes(updated: Record<TLShapeId, [from: TLRecord, to: TLRecord]>) {
    Object.values(updated).forEach(([_, newRecord]) => {
      const shape = newRecord as TLShape;
      if (shape && shape.type === 'visual-chronology-node') {
        this.handleNodeUpdated(shape as VisualChronologyNodeShape);

        trackCustomEvent(TrackingEventsV2.EditVisualChronologyNode, {
          matterId: this.options.matterId,
          subsetId: this.options.subsetId,
          chronologyId: this.options.chronologyId,
        });
      } else if (shape && this.isAnnotationShape(shape.type)) {
        this.handleAnnotationUpdated(shape);
      }
    });
  }

  private handleRemovedShapes(removed: Record<TLShapeId, TLRecord>) {
    Object.values(removed).forEach((record: TLRecord) => {
      const shape = record as TLShape;
      if (shape.type === 'visual-chronology-node') {
        this.handleNodeRemoved(shape as VisualChronologyNodeShape);

        trackCustomEvent(TrackingEventsV2.DeleteVisualChronologyNode, {
          matterId: this.options.matterId,
          subsetId: this.options.subsetId,
          chronologyId: this.options.chronologyId,
        });
      } else if (this.isAnnotationShape(shape.type)) {
        this.handleAnnotationRemoved(shape);
      }
    });
  }

  private handleNodeAdded(node: VisualChronologyNodeShape) {
    // Check if this is a new node (not in original nodes)
    if (!this.originalNodes.has(node.id)) {
      const nodeProps = node.props;

      // Create pending change for new node
      const nodeId = node.id.split('shape:visual-chronology-node-')[1];

      this.pendingChanges.addCreateChange(nodeId, {
        description: nodeProps.description ?? '',
        factDateText: nodeProps.dateText ?? '',
        docId: nodeProps.docId,
      });

      // Store this node in the original nodes map
      this.originalNodes.set(node.id, { ...node });
    }
  }

  private handleNodeUpdated(node: VisualChronologyNodeShape) {
    const originalNode = this.originalNodes.get(node.id);

    if (!originalNode) {
      return;
    }

    // Get the original node so we can compare the changes
    const originalProps = originalNode.props;
    const currentProps = node.props;

    // Check if date or description has changed
    const propsChanged =
      currentProps.dateText !== originalProps.dateText || currentProps.description !== originalProps.description;
    if (!propsChanged) {
      // Only position/visual changes, ignore (update is called on positional changes as well as content changes)
      return;
    }

    // Build the changes object
    const changes: { description: string | undefined; factDateText: string | undefined } = {
      description: undefined,
      factDateText: undefined,
    };
    if (currentProps.description !== originalProps.description) {
      changes.description = currentProps.description;
    }
    if (currentProps.dateText !== originalProps.dateText) {
      changes.factDateText = currentProps.dateText;
    }

    const nodeId = node.id.split('shape:visual-chronology-node-')[1];
    this.pendingChanges.addUpdateChange(nodeId, changes);
  }

  private handleNodeRemoved(node: VisualChronologyNodeShape) {
    const originalNode = this.originalNodes.get(node.id);

    if (originalNode) {
      // Add id to list of ids to be deleted
      const nodeId = node.id.split('shape:visual-chronology-node-')[1];
      this.pendingChanges.addDeleteChange(nodeId);
    }

    // Remove from original nodes tracking
    this.originalNodes.delete(node.id);
  }

  // Helper method to identify annotation shapes (text, notes, arrows, highlights, etc.)
  private isAnnotationShape(shapeType: string): boolean {
    const annotationShapes = [
      'text',
      'note',
      'arrow',
      'highlight',
      'geo',
      'draw',
      'frame',
      'embed',
      'image',
      'video',
      'bookmark',
    ];

    // Exclude visualisation-specific shapes that are not user annotations
    const excludedShapes = [
      'visual-chronology-node',
      'year-marker',
      'add-new-node-button',
      'add-in-between-node-button',
    ];

    return annotationShapes.includes(shapeType) && !excludedShapes.includes(shapeType);
  }

  // Handle annotation shape added
  private handleAnnotationAdded(shape: TLShape) {
    const shapeType = this.mapShapeTypeToAnnotationType(shape.type);
    this.pendingChanges.addAnnotationChange(shape.id, shapeType);
  }

  // Handle annotation shape updated
  private handleAnnotationUpdated(shape: TLShape) {
    const shapeType = this.mapShapeTypeToAnnotationType(shape.type);
    this.pendingChanges.addAnnotationChange(shape.id, shapeType);
  }

  // Handle annotation shape removed
  private handleAnnotationRemoved(shape: TLShape) {
    // Remove the annotation change from pending changes
    const shapeType = this.mapShapeTypeToAnnotationType(shape.type);
    this.pendingChanges.addAnnotationChange(shape.id, shapeType);
  }

  // Map TLDraw shape types to our annotation types
  private mapShapeTypeToAnnotationType(shapeType: string): 'text' | 'note' | 'arrow' | 'highlight' | 'geo' | 'other' {
    switch (shapeType) {
      case 'text':
        return 'text';
      case 'note':
        return 'note';
      case 'arrow':
        return 'arrow';
      case 'highlight':
        return 'highlight';
      case 'geo':
        return 'geo';
      default:
        return 'other';
    }
  }

  public getChangeCount(): number {
    return this.pendingChanges.getChangeCount();
  }

  public hasUnsavedChanges(): boolean {
    return this.pendingChanges.hasUnsavedChanges();
  }

  public clearAllChanges() {
    this.pendingChanges.clearAllChanges();
  }

  public getPendingChanges(): VisualisationPendingChanges {
    return this.pendingChanges;
  }

  public dispose() {
    // Clean up store listener
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }

    // Dispose of the pending changes instance
    this.pendingChanges.dispose();
  }

  // Public methods to control change tracking lifecycle
  public pauseTracking() {
    this.isPaused = true;
  }

  public resumeTracking() {
    this.isPaused = false;
  }

  public startTracking() {
    // Capture initial state of all nodes
    this.captureInitialState();

    // Start listening for shape changes
    if (!this.storeUnsubscribe) {
      this.storeUnsubscribe = this.editor.store.listen(this.handleStoreChange.bind(this), { scope: 'document' });
    }

    // Resume tracking
    this.resumeTracking();
  }
}
